/**
 * StatusPill — the four canonical UI indicator pills from the brand sheet:
 *
 *   • COMPLIANT      (emerald)  — observation resolved, control in place
 *   • OBSERVATION    (amber)    — recommended/desirable finding
 *   • NON-COMPLIANT  (red)      — statutory deficiency, must-fix
 *   • REFERENCE      (blue)     — link to a standard / regulation / guidance
 *
 * Used wherever inspection state needs to be expressed at a glance — list
 * rows, observation cards, the AI report header, the onboarding tour. One
 * canonical visual makes the product feel coherent across surfaces.
 */
import React from 'react';
import { CheckCircle2, AlertTriangle, AlertOctagon, Info } from 'lucide-react';

export type StatusVariant = 'compliant' | 'observation' | 'non_compliant' | 'reference';

interface Props {
  variant: StatusVariant;
  /** Override the default label ("COMPLIANT", etc). */
  label?: string;
  /** "sm" for inline badges, "md" for list rows, "lg" for hero labels. */
  size?: 'sm' | 'md' | 'lg';
  /** Hide the icon and show only the text. */
  iconless?: boolean;
  className?: string;
  title?: string;
}

const VARIANTS: Record<
  StatusVariant,
  { label: string; cls: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  compliant: {
    label: 'COMPLIANT',
    cls: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300',
    Icon: CheckCircle2,
  },
  observation: {
    label: 'OBSERVATION',
    cls: 'bg-amber-500/10 border-amber-500/40 text-amber-300',
    Icon: AlertTriangle,
  },
  non_compliant: {
    label: 'NON-COMPLIANT',
    cls: 'bg-red-500/10 border-red-500/40 text-red-300',
    Icon: AlertOctagon,
  },
  reference: {
    label: 'REFERENCE',
    cls: 'bg-blue-500/10 border-blue-500/40 text-blue-300',
    Icon: Info,
  },
};

const SIZE_CLS: Record<NonNullable<Props['size']>, { pad: string; text: string; icon: string }> = {
  sm: { pad: 'px-2   py-0.5', text: 'text-[10px]', icon: 'w-3 h-3'      },
  md: { pad: 'px-2.5 py-1',   text: 'text-[11px]', icon: 'w-3.5 h-3.5'  },
  lg: { pad: 'px-3   py-1.5', text: 'text-xs',     icon: 'w-4 h-4'      },
};

export default function StatusPill({
  variant,
  label,
  size = 'md',
  iconless,
  className = '',
  title,
}: Props) {
  const v = VARIANTS[variant];
  const s = SIZE_CLS[size];
  return (
    <span
      title={title ?? v.label}
      className={[
        'inline-flex items-center gap-1.5 rounded-full border font-bold uppercase tracking-wider',
        v.cls,
        s.pad,
        s.text,
        className,
      ].join(' ')}
    >
      {!iconless && <v.Icon className={s.icon} />}
      <span>{label ?? v.label}</span>
    </span>
  );
}

/** Map a CDI/SIRE category code (S/R/D/NS) to the correct StatusPill variant. */
export function statusVariantForCategory(
  category: string | null | undefined,
): StatusVariant {
  switch ((category ?? '').toUpperCase()) {
    case 'S':  return 'non_compliant'; // Statutory — must-fix
    case 'R':  return 'observation';   // Recommended
    case 'D':  return 'observation';   // Desirable
    case 'NS': return 'reference';     // Not specified / informational
    default:   return 'reference';
  }
}
